// src/components/common/LoadingPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Componente de página de carga con funcionalidad de timeout
 * @param {Object} props
 * @param {string} props.message - Mensaje a mostrar durante la carga
 * @param {number} props.timeout - Tiempo en ms después del cual se muestra opción de salida de emergencia
 * @param {string} props.fallbackPath - Ruta a la que redirigir si el usuario decide cancelar
 */
const LoadingPage = ({ 
  message = 'Cargando...', 
  timeout = 15000,  // 15 segundos
  fallbackPath = '/login'
}) => {
  const navigate = useNavigate();
  const [showFallback, setShowFallback] = useState(false);
  const [dots, setDots] = useState('');
  
  // Efecto para mostrar opción de salida después del timeout
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setShowFallback(true);
    }, timeout);
    
    return () => clearTimeout(timeoutId);
  }, [timeout]);
  
  // Efecto para animación de puntos suspensivos
  useEffect(() => {
    const intervalId = setInterval(() => {
      setDots(prev => {
        if (prev.length >= 3) return '';
        return prev + '.';
      });
    }, 500);
    
    return () => clearInterval(intervalId);
  }, []);

  // Función para redirigir a la ruta de fallback
  const handleFallback = () => {
    navigate(fallbackPath, { replace: true });
  };

  return (
    <div className="min-h-screen bg-dark-900 flex flex-col items-center justify-center px-4">
      <div className="text-center">
        {/* Spinner de carga */}
        <div className="w-16 h-16 border-4 border-primary-400 border-t-transparent rounded-full animate-spin mx-auto mb-6"></div>
        
        {/* Mensaje de carga con puntos animados */}
        <h2 className="text-xl text-white font-semibold mb-4">
          {message}{dots}
        </h2>
        
        {/* Mensaje de espera */}
        <p className="text-gray-400 mb-6 max-w-md mx-auto">
          Esto podría tomar unos momentos mientras establecemos conexión segura con el servidor.
        </p>
        
        {/* Opción de salida después del timeout */}
        {showFallback && (
          <div className="mt-8 space-y-4">
            <p className="text-yellow-400">
              La operación está tomando más tiempo de lo esperado.
            </p>
            <button
              onClick={handleFallback}
              className="px-6 py-2 bg-dark-700 hover:bg-dark-600 text-white rounded-lg transition-colors"
            >
              Cancelar y volver
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoadingPage;